import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SupportTicket } from '@/lib/api/types/supportTicket';

interface SupportState {
  tickets: SupportTicket[];
  loading: boolean;
  error: string | null;
}

const initialState: SupportState = {
  tickets: [],
  loading: false,
  error: null,
};

export const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    // Start loading tickets
    fetchSupportTicketsStart: (state) => {
      state.loading = true;
      state.error = null;
    },

    // Successfully loaded tickets
    fetchSupportTicketsSuccess: (
      state,
      action: PayloadAction<SupportTicket[]>
    ) => {
      state.tickets = action.payload;
      state.loading = false;
      state.error = null;
    },

    // Failed to load tickets
    fetchSupportTicketsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create a new ticket - optimistic update
    createSupportTicketRequest: (
      state,
      action: PayloadAction<SupportTicket>
    ) => {
      state.tickets.unshift(action.payload);
    },

    // Create ticket successful
    createSupportTicketSuccess: (
      state,
      action: PayloadAction<SupportTicket>
    ) => {
      // Find and replace the optimistically added ticket
      const index = state.tickets.findIndex(
        (ticket) => ticket.ticket_uid === action.payload.ticket_uid
      );
      if (index !== -1) {
        state.tickets[index] = action.payload;
      } else {
        state.tickets.unshift(action.payload);
      }
    },

    // Create ticket failed
    createSupportTicketFailure: (
      state,
      action: PayloadAction<{ error: string; ticketUid: string }>
    ) => {
      // Remove the optimistically added ticket
      state.tickets = state.tickets.filter(
        (ticket) => ticket.ticket_uid !== action.payload.ticketUid
      );
      state.error = action.payload.error;
    },

    // Clear tickets
    clearSupportTickets: (state) => {
      state.tickets = [];
    },

    // Clear error
    clearSupportError: (state) => {
      state.error = null;
    },
  },
});

export const {
  fetchSupportTicketsStart,
  fetchSupportTicketsSuccess,
  fetchSupportTicketsFailure,
  createSupportTicketRequest,
  createSupportTicketSuccess,
  createSupportTicketFailure,
  clearSupportTickets,
  clearSupportError,
} = supportSlice.actions;

export default supportSlice.reducer;
